/**
 * The Customer Support Overview (WALLIYO-0132), built to the approved design.
 *
 * The page is a clock. Cases are ordered by how long they have left rather than
 * when they arrived, each named by what the customer called it rather than by
 * its number, and the queue nobody owns gets a panel of its own — it is the one
 * thing here that gets worse with nobody doing anything.
 */

import type { CaseRow, SupportOverview as Data } from "@wallyo/contracts/console/overview";

import { t, tCount, type MessageId } from "../../i18n";
import { formatNumber } from "../../i18n/format";
import { Figures } from "./OverviewScreen";

export function SupportOverviewView({ data }: { data: Data }) {
  return (
    <>
      <Figures figures={data.figures} />

      <div className="ov-split">
        <section className="card">
          <div className="ov-card-head">
            <h2>{t("overview.support.runningOut")}</h2>
            <span className="foot">{t("overview.support.soonestFirst")}</span>
          </div>
          {data.dueSoon.length === 0 ? (
            <p className="foot">{t("overview.support.nothingDue")}</p>
          ) : (
            data.dueSoon.map((row) => <Case key={row.reference} row={row} />)
          )}
        </section>

        <div className="ov-side">
          <section className="card">
            <h2>{t("overview.support.whereFrom")}</h2>
            {data.channels.length === 0 ? (
              <p className="foot">{t("overview.support.noChannels")}</p>
            ) : (
              data.channels.map((channel) => (
                <div key={channel.tallyId} className="ov-line">
                  <span className="grow">{t(`overview.support.channel.${channel.tallyId}` as MessageId)}</span>
                  <span className="mono">{formatNumber(channel.count)}</span>
                </div>
              ))
            )}
          </section>

          {data.unclaimed ? (
            <section className="card ov-callout">
              <h2>{t("overview.support.nobodyOwns", { count: formatNumber(data.unclaimed.count) })}</h2>
              <p className="foot">
                {tCount("overview.support.nobodyOwnsWhy", data.unclaimed.oldestDays, {
                  days: formatNumber(data.unclaimed.oldestDays),
                })}
              </p>
              <a className="go-link" href="#cases">
                {t("overview.support.takeOldest")}
              </a>
            </section>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * One case: what the customer called it, how long is left, and whose it is.
 *
 * The bar down the side carries the urgency, so a row reads as urgent before
 * anybody has read the words on it.
 */
function Case({ row }: { row: CaseRow }) {
  return (
    <div className="ov-row">
      <span className={`ov-bar-side ${row.tone}`} aria-hidden="true" />
      <span className="grow">
        <span className="ov-row-what">{row.subject}</span>
        <span
          className={
            row.tone === "bad" ? "ov-row-why danger" : row.tone === "warn" ? "ov-row-why warn-text" : "ov-row-why"
          }
        >
          <span className="mono ov-ref">{row.reference}</span> · {left(row.minutesLeft)} ·{" "}
          {t(`overview.support.row.${row.rowId}` as MessageId)}
        </span>
      </span>
      <a className={row.tone === "bad" ? "btn primary" : "btn"} href={row.href}>
        {t(`overview.action.${row.actionId}` as MessageId)}
      </a>
    </div>
  );
}

/** "due in 40 minutes", "due in 3 hours", or that the deadline has gone. */
function left(minutes: number): string {
  if (minutes < 0) {
    const over = Math.abs(minutes);
    return tCount("overview.support.overdue", over, { minutes: formatNumber(over) });
  }
  if (minutes < 60) return tCount("overview.support.dueInMinutes", minutes, { minutes: formatNumber(minutes) });
  const hours = Math.round(minutes / 60);
  return tCount("overview.support.dueInHours", hours, { hours: formatNumber(hours) });
}
